import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Phone, Mail, Globe, Linkedin, MapPin } from "lucide-react";

/**
 * Sebyx visiting card — front + back
 * Standard business card: 88mm × 55mm (3.5" × 2") at 300dpi
 * Page is print-friendly: hide UI chrome via @media print
 */
export default function VisitingCard() {
  return (
    <main
      data-testid="visiting-card-page"
      className="min-h-screen bg-sebyx-bg text-sebyx-text font-body py-16 px-6 md:px-12"
    >
      {/* Top nav (hidden on print) */}
      <header className="print:hidden max-w-4xl mx-auto mb-12 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-3 group"
        >
          <ArrowLeft
            size={16}
            className="text-sebyx-accent group-hover:-translate-x-0.5 transition-transform"
          />
          <span className="block w-2 h-2 bg-sebyx-accent" />
          <span className="font-display text-xl font-semibold tracking-tight">
            Sebyx
          </span>
        </Link>
        <button
          type="button"
          onClick={() => window.print()}
          className="px-5 py-2 bg-sebyx-accent text-sebyx-bg font-semibold text-sm hover:bg-[#3FA097] transition-colors"
        >
          Print / Save as PDF
        </button>
      </header>

      <div className="print:hidden max-w-4xl mx-auto mb-10">
        <div className="flex items-center gap-3 mb-4">
          <span className="block w-8 h-px bg-sebyx-accent" />
          <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-sebyx-accent">
            Visiting Card · 88mm × 55mm
          </span>
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tighter">
          Print-ready · front and back
        </h1>
        <p className="mt-4 text-sebyx-muted text-sm leading-relaxed max-w-2xl">
          Click <span className="text-sebyx-accent">Print / Save as PDF</span> to export.
          In the print dialog: Layout = <em>Landscape</em>, Margins = <em>None</em>,
          Scale = 100%. Or screenshot each card and send to your printer at 300dpi.
        </p>
      </div>

      {/* Cards */}
      <div className="max-w-4xl mx-auto space-y-12 print:space-y-6">
        {/* FRONT */}
        <CardWrapper label="Front">
          <div className="relative w-full h-full p-8 sebyx-corner overflow-hidden">
            {/* faint grid bg */}
            <div className="absolute inset-0 sebyx-grid-bg opacity-40 pointer-events-none" />
            {/* accent corner mark */}
            <div className="absolute top-0 right-0 flex flex-col items-end p-3">
              <span className="font-mono text-[7px] tracking-[0.3em] uppercase text-sebyx-accent">
                [01] Revenue Architecture
              </span>
            </div>

            <div className="relative h-full flex flex-col justify-between">
              {/* wordmark */}
              <div className="flex items-center gap-2">
                <span className="block w-2 h-2 bg-sebyx-accent" />
                <span className="font-display text-2xl font-semibold tracking-tight text-sebyx-text">
                  Sebyx
                </span>
              </div>

              {/* identity */}
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <span className="block w-6 h-px bg-sebyx-accent" />
                  <span className="font-mono text-[8px] tracking-[0.3em] uppercase text-sebyx-accent">
                    Founder & Strategic Architect
                  </span>
                </div>
                <h2 className="font-display text-2xl font-semibold tracking-tight text-sebyx-text leading-none">
                  Mandar Prabhudesai
                </h2>
                <p className="font-mono text-[8px] tracking-[0.25em] uppercase text-sebyx-muted leading-relaxed pt-1">
                  Sales Breakthroughs <span className="text-sebyx-accent">/</span>{" "}
                  Engineered.
                </p>
              </div>
            </div>
          </div>
        </CardWrapper>

        {/* BACK */}
        <CardWrapper label="Back">
          <div className="relative w-full h-full p-7 sebyx-corner overflow-hidden">
            <div className="absolute inset-0 sebyx-grid-bg opacity-30 pointer-events-none" />

            <div className="relative h-full grid grid-rows-[auto_1fr_auto] gap-3">
              {/* eyebrow */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="block w-1.5 h-1.5 bg-sebyx-accent" />
                  <span className="font-display text-base font-semibold tracking-tight">
                    Sebyx
                  </span>
                </div>
                <span className="font-mono text-[7px] tracking-[0.3em] uppercase text-sebyx-muted">
                  Engineered in Bengaluru
                </span>
              </div>

              {/* contact grid */}
              <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 self-center">
                <ContactRow
                  icon={Phone}
                  label="Direct"
                  value="+91 91084 36667"
                />
                <ContactRow
                  icon={Mail}
                  label="Email"
                  value="contact@sebyx.com"
                />
                <ContactRow
                  icon={Globe}
                  label="Web"
                  value="www.sebyx.com"
                />
                <ContactRow
                  icon={Linkedin}
                  label="LinkedIn"
                  value="/in/mandarprabhudesai"
                />
                <ContactRow
                  icon={MapPin}
                  label="Hubs"
                  value="KA · MH · GJ"
                  span={2}
                />
              </div>

              {/* legal strip */}
              <div className="border-t border-sebyx-border pt-2 flex items-center justify-between">
                <span className="font-mono text-[6px] tracking-[0.3em] uppercase text-sebyx-muted leading-relaxed">
                  Brand of Serio Healthcare
                </span>
                <span className="font-mono text-[6px] tracking-[0.3em] uppercase text-sebyx-muted">
                  GSTIN 29APZPP8620D1Z8
                </span>
              </div>
            </div>
          </div>
        </CardWrapper>
      </div>

      <p className="print:hidden max-w-4xl mx-auto mt-12 font-mono text-[10px] tracking-[0.2em] uppercase text-sebyx-muted leading-relaxed">
        * Tip · Ask your printer for matte uncoated stock at 350gsm + a thin
        spot-UV finish on the Cyber Teal mark for a premium feel.
      </p>
    </main>
  );
}

function CardWrapper({ children, label }) {
  return (
    <figure className="space-y-3">
      <figcaption className="print:hidden font-mono text-[10px] tracking-[0.3em] uppercase text-sebyx-muted">
        // {label}
      </figcaption>
      <div
        // 88mm × 55mm at 4× preview scale → 352mm × 220mm? No, render at print size + scale up visually.
        className="bg-sebyx-bg border border-sebyx-border shadow-[0_30px_80px_-30px_rgba(79,185,175,0.15)]"
        style={{ width: "88mm", height: "55mm" }}
      >
        {children}
      </div>
    </figure>
  );
}

function ContactRow({ icon: Icon, label, value, span }) {
  return (
    <div
      className={`flex items-start gap-2.5 ${span === 2 ? "col-span-2" : ""}`}
    >
      <div className="w-5 h-5 border border-sebyx-border flex items-center justify-center shrink-0 mt-0.5">
        <Icon size={9} strokeWidth={1.5} className="text-sebyx-accent" />
      </div>
      <div className="flex flex-col leading-tight">
        <span className="font-mono text-[6.5px] tracking-[0.3em] uppercase text-sebyx-muted">
          {label}
        </span>
        <span className="font-display text-[11px] text-sebyx-text leading-snug">
          {value}
        </span>
      </div>
    </div>
  );
}
